import React, { useState, useCallback, useEffect } from "react"
import { Form, Button, Row, Col, Card, Spinner, InputGroup, CloseButton } from "react-bootstrap"
import { useNavigate, Link } from "react-router-dom"
import { toast } from "react-toastify"
import useAuthAxios from "../hooks/useAuthAxios"
import Footer from "../components/layout/Footer"
import logo from "../assets/images/logo.jpg"
import { useAuth } from "../context/AuthContext"
import { validatePassword } from "../utils/validationUtils"
import { validateEmail } from "../utils/validationUtils"

const Registration = () => {
  const [formData, setFormData] = useState({
    garageName: "",
    garageStreet: "",
    garageNumber: "",
    garageCity: "",
    garagePostalCode: "",
    garagePhone1: "",
    garagePhone2: "",
    contactLastName: "",
    contactFirstName: "",
    contactPhone: "",
    contactMobile: "",
    contactEmail: "",
    vatNumber: "",
    taxOffice: "",
    gemi: "",
    username: "",
    password: ""
  })

  const [bankAccounts, setBankAccounts] = useState([{ bank: "", iban: "" }])
  const [files, setFiles] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [passwordValidation, setPasswordValidation] = useState({ isValid: true, errors: [] })
  const navigate = useNavigate()
  const { post } = useAuthAxios()
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/")
    }
  }, [isAuthenticated, navigate])

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Validate password in real-time
    if (name === "password") {
      const validation = validatePassword(value)
      setPasswordValidation(validation)
    }
  }

  const handleBankAccountChange = (index, e) => {
    const { name, value } = e.target
    const newBankAccounts = [...bankAccounts]
    newBankAccounts[index][name] = value
    setBankAccounts(newBankAccounts)
  }

  const addBankAccount = () => {
    setBankAccounts([...bankAccounts, { bank: "", iban: "" }])
  }

  const removeBankAccount = index => {
    const newBankAccounts = [...bankAccounts]
    newBankAccounts.splice(index, 1)
    setBankAccounts(newBankAccounts)
  }

  const handleFileChange = e => {
    const newFiles = Array.from(e.target.files)
    const validFiles = newFiles.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        toast.error(`Το αρχείο ${file.name} είναι πολύ μεγάλο ( > 10MB).`)
        return false
      }
      return true
    })
    setFiles(prev => [...prev, ...validFiles])
  }

  const removeFile = fileToRemove => {
    setFiles(prev => prev.filter(file => file !== fileToRemove))
  }

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      // Validate password before submission
      if (!passwordValidation.isValid) {
        toast.error("Παρακαλώ διορθώστε τον κωδικό πρόσβασης: " + passwordValidation.errors.join(", "))
        return
      }

      setIsSubmitting(true)

      try {
        const filePromises = files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () =>
              resolve({
                fileName: file.name,
                fileContent: reader.result.split(",")[1]
              })
            reader.onerror = error => reject(error)
          })
        })

        const fileContents = await Promise.all(filePromises)

        const payload = {
          ...formData,
          bankAccounts,
          photos: fileContents
        }

        const response = await post("/register", payload)

        // Improved error handling based on IsSuccess, Problem, Warning
        if (response.data?.IsSuccess) {
          toast.success("Η εγγραφή σας ολοκληρώθηκε με επιτυχία.")
          // Optionally show warning if present
          if (response.data.Warning) {
            toast.warn(response.data.Warning)
          }
          navigate("/login")
        } else if (response.data?.Problem) {
          toast.error(response.data.Problem)
          // Optionally show warning if present
          if (response.data.Warning) {
            toast.warn(response.data.Warning)
          }
        }
      } catch (error) {
        toast.error("Παρουσιάστηκε σφάλμα κατά την αποστολή της φόρμας.")
      } finally {
        setIsSubmitting(false)
      }
    },
    [formData, bankAccounts, files, post, navigate, passwordValidation]
  )

  return (
    <div className="grid-background registration-page">
      <div className="min-vh-100 d-flex flex-column registration-page">
        <div className="flex-grow-1 d-flex align-items-center py-5">
          <div className="container-fluid">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={7}>
                <div className="text-center mb-4">
                  <Link to="/">
                    <img src={logo} alt="eGarages logo" className="registration-logo" />
                  </Link>
                </div>
                <Card className="content-glass">
                  <Card.Header>
                    <h4 className="mb-0 text-center">Καταχώρηση νέου συνεργείου</h4>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                      {/* Garage Details */}
                      <h5 className="section-title">Στοιχεία Συνεργείου</h5>
                      <Row className="g-3 mb-4">
                        <Col md={6}>
                          <Form.Control
                            name="garageName"
                            placeholder="Επωνυμία"
                            value={formData.garageName}
                            onChange={handleInputChange}
                            isInvalid={formData.garageName === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="garageStreet"
                            placeholder="Οδός"
                            value={formData.garageStreet}
                            onChange={handleInputChange}
                            isInvalid={formData.garageStreet === ""}
                            required
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            name="garageNumber"
                            placeholder="Αριθμός"
                            value={formData.garageNumber}
                            onChange={handleInputChange}
                            isInvalid={formData.garageNumber === ""}
                            required
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            name="garageCity"
                            placeholder="Πόλη"
                            value={formData.garageCity}
                            onChange={handleInputChange}
                            isInvalid={formData.garageCity === ""}
                            required
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control
                            name="garagePostalCode"
                            placeholder="Τ.Κ."
                            value={formData.garagePostalCode}
                            onChange={handleInputChange}
                            isInvalid={formData.garagePostalCode === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="garagePhone1"
                            placeholder="Τηλέφωνο 1"
                            value={formData.garagePhone1}
                            onChange={handleInputChange}
                            isInvalid={formData.garagePhone1 === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="garagePhone2"
                            placeholder="Τηλέφωνο 2"
                            value={formData.garagePhone2}
                            onChange={handleInputChange}
                          />
                        </Col>
                      </Row>

                      {/* Contact Person Details */}
                      <h5 className="section-title">Στοιχεία Υπεύθυνου</h5>
                      <Row className="g-3 mb-4">
                        <Col md={6}>
                          <Form.Control
                            name="contactLastName"
                            placeholder="Επώνυμο"
                            value={formData.contactLastName}
                            onChange={handleInputChange}
                            isInvalid={formData.contactLastName === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="contactFirstName"
                            placeholder="Όνομα"
                            value={formData.contactFirstName}
                            onChange={handleInputChange}
                            isInvalid={formData.contactFirstName === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="contactPhone"
                            placeholder="Τηλέφωνο"
                            value={formData.contactPhone}
                            onChange={handleInputChange}
                            isInvalid={formData.contactPhone === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="contactMobile"
                            placeholder="Κινητό"
                            value={formData.contactMobile}
                            onChange={handleInputChange}
                            isInvalid={formData.contactMobile === ""}
                          />
                        </Col>
                        <Col xs={12}>
                          <Form.Control
                            name="contactEmail"
                            type="email"
                            placeholder="Email"
                            value={formData.contactEmail}
                            onChange={handleInputChange}
                            isInvalid={
                              formData.contactEmail === "" || !validateEmail(formData.contactEmail).isValid
                            }
                            required
                          />
                        </Col>
                      </Row>

                      {/* Financial Details */}
                      <h5 className="section-title">Οικονομικά Στοιχεία</h5>
                      <Row className="g-3 mb-4">
                        <Col md={6}>
                          <Form.Control
                            name="vatNumber"
                            placeholder="Α.Φ.Μ."
                            value={formData.vatNumber}
                            onChange={handleInputChange}
                            isInvalid={formData.vatNumber === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="taxOffice"
                            placeholder="Δ.Ο.Υ."
                            value={formData.taxOffice}
                            onChange={handleInputChange}
                            isInvalid={formData.taxOffice === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="gemi"
                            placeholder="Γ.Ε.Μ.Η."
                            value={formData.gemi}
                            onChange={handleInputChange}
                            isInvalid={formData.gemi === ""}
                            required
                          />
                        </Col>
                        {bankAccounts.map((account, index) => (
                          <Col xs={12} key={index}>
                            <InputGroup>
                              <Form.Control
                                name="bank"
                                placeholder="Τράπεζα"
                                value={account.bank}
                                onChange={e => handleBankAccountChange(index, e)}
                                isInvalid={account.bank === ""}
                                required
                              />
                              <Form.Control
                                name="iban"
                                placeholder="IBAN"
                                value={account.iban}
                                onChange={e => handleBankAccountChange(index, e)}
                                isInvalid={account.iban === ""}
                                required
                              />
                              {bankAccounts.length > 1 && (
                                <Button variant="outline-danger" onClick={() => removeBankAccount(index)}>
                                  <i className="fas fa-trash"></i>
                                </Button>
                              )}
                            </InputGroup>
                          </Col>
                        ))}
                        <Col xs={12}>
                          <Button variant="outline-primary" size="sm" onClick={addBankAccount}>
                            <i className="fas fa-plus me-2"></i>Προσθήκη Νέου Λογαριασμού IBAN
                          </Button>
                        </Col>
                      </Row>

                      {/* Login Details */}
                      <h5 className="section-title">Στοιχεία Σύνδεσης</h5>
                      <Row className="g-3 mb-4">
                        <Col md={6}>
                          <Form.Control
                            name="username"
                            placeholder="Όνομα χρήστη"
                            value={formData.username}
                            onChange={handleInputChange}
                            isInvalid={formData.username === ""}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Control
                            name="password"
                            type="password"
                            placeholder="Κωδικός"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            className={
                              formData.password === "" || (!passwordValidation.isValid && formData.password)
                                ? "is-invalid"
                                : ""
                            }
                          />
                          {!passwordValidation.isValid && formData.password && (
                            <div className="invalid-feedback">
                              {passwordValidation.errors.map((error, index) => (
                                <div key={index}>{error}</div>
                              ))}
                            </div>
                          )}
                        </Col>
                      </Row>

                      {/* File Upload */}
                      <h5 className="section-title">Φωτογραφίες Συνεργείου</h5>
                      <Form.Group className="mb-4">
                        <Form.Control type="file" multiple onChange={handleFileChange} />
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {files.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              <i className="fas fa-image file-icon"></i>
                              <span className="file-name">{file.name}</span>
                              <CloseButton onClick={() => removeFile(file)} />
                            </div>
                          ))}
                        </div>
                      </Form.Group>

                      <div className="d-grid mt-4">
                        <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                          {isSubmitting ? <Spinner animation="border" size="sm" /> : "Υποβολή Εγγραφής"}
                        </Button>
                      </div>
                      <div className="text-center mt-3">
                        <Link to="/login">Έχετε ήδη λογαριασμό; Πατήστε εδώ για να συνδεθείτε.</Link>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  )
}

export default Registration
